/* eslint-disable */
import * as React from "react";
import { WithStyles, withStyles } from "@material-ui/styles";
import { MessageDescriptor } from "react-intl";
import { Typography, ListItem, ExpansionPanel, AccordionDetails, AccordionSummary, SvgIcon } from "@material-ui/core";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";

import { mainContentItem, mainContentTableDefinition, mainContentParagraph, linkDefinition } from "../constants/types";
import messages from "../constants/messages";
import { styles } from "../constants/styles";
import { ArrowUpward } from "@material-ui/icons";
import "../constants/styleSheet.css";
import { FormattedHTMLMessage } from "../../../modules/locale";

type OwnProps = {
  item: mainContentItem;
  collapsedPanels: Set<string>;
  setCollapsedPanels: (collapsedPanels: Set<string>) => void;
  refCallback: (element: any) => void;
  scrollToTop: () => void;
};

type Props = OwnProps & WithStyles<typeof styles>;

const MainContent: React.FC<Props> = (props: Props) => {
  const { classes, item, refCallback, scrollToTop, collapsedPanels, setCollapsedPanels } = props;

  const renderParagraph = (paragraph: mainContentParagraph) => {
    let componentToRender = <span>Default</span>;
    switch (paragraph.type) {
      case "text":
        const message = paragraph.content as MessageDescriptor;
        componentToRender = (
          <Typography key={paragraph.id} className={classes.paragraphText}>
            <FormattedHTMLMessage {...message} />
          </Typography>
        );
        break;
      case "list":
        componentToRender = (
          <ul key={paragraph.id}>
            {(paragraph.content as MessageDescriptor[]).map((item, index) => (
              <li key={`${paragraph.id}.item${index}`}>
                <Typography className={classes.paragraphText}>
                  <FormattedHTMLMessage {...item} />
                </Typography>
              </li>
            ))}
          </ul>
        );
        break;
      case "table":
        componentToRender = (
          <table key={paragraph.id} className={classes.paragraphTableRoot}>
            <thead>
              <tr>
                {(paragraph.content as mainContentTableDefinition).headers.map((header, index) => (
                  <td key={`${paragraph.id}header${index}`} className={classes.paragraphTableItem}>
                    <Typography>
                      <FormattedHTMLMessage {...header} />
                    </Typography>
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {(paragraph.content as mainContentTableDefinition).rows.map((row, index) => (
                <>
                  <tr key={`${paragraph.id}.Row.${index}`}>
                    <td className={classes.paragraphTableItem}>
                      <Typography className={classes.paragraphTableTypo}>
                        <FormattedHTMLMessage {...row.column1} />
                      </Typography>
                    </td>
                    {row.column2.content.map((item, index) =>
                      row.column2.type === "list" ? (
                        <tr>
                          <td className={classes.paragraphTableMultiRowItem}>
                            <Typography className={classes.paragraphTableTypo}>
                              <FormattedHTMLMessage {...item} />
                            </Typography>
                          </td>
                        </tr>
                      ) : (
                        <tr>
                          <td className={classes.paragraphTableMultiRowItem}>
                            <a
                              key={`${paragraph.id}.Row.${index}`}
                              target="_blank"
                              href={(item as linkDefinition).path}
                              className={"HelpPage-Link"}
                            >
                              <Typography className={classes.paragraphLink}>{(item as linkDefinition).text}</Typography>
                            </a>
                          </td>
                        </tr>
                      )
                    )}
                  </tr>
                  <></>
                </>
              ))}
            </tbody>
          </table>
        );
        break;
      case "title":
        const title = paragraph.content as MessageDescriptor;
        componentToRender = (
          <Typography key={paragraph.id} className={classes.paragraphTitle}>
            <FormattedHTMLMessage {...title} />
          </Typography>
        );
        break;
      case "link":
        componentToRender = (
          <a key={paragraph.id} target="_blank" href={(paragraph.content as linkDefinition).path} className={"HelpPage-Link"}>
            <Typography className={classes.paragraphLink}>{(paragraph.content as linkDefinition).text}</Typography>
          </a>
        );
        break;
      case "download":
        componentToRender = (
          <a
            key={paragraph.id}
            href={`${process.env.PUBLIC_URL}${(paragraph.content as linkDefinition).path}`}
            target="_blank"
            className={"HelpPage-Link"}
            download
          >
            <Typography className={classes.paragraphLink}>{(paragraph.content as linkDefinition).text}</Typography>
          </a>
        );
        break;
    }
    return componentToRender;
  };

  const setExpansionState = (id: string) => (event: any, expanded: boolean) => {
    const newSet = new Set(collapsedPanels);
    if (expanded) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setCollapsedPanels(newSet);
  };

  return (
    <>
      <ListItem key={item.id} ref={refCallback} className={classes.mainContentListItem}>
        <Typography className={classes.mainContentItemName}>
          <FormattedHTMLMessage {...item.name} />
        </Typography>
      </ListItem>
      {item.sections.map((section) => (
        <ListItem key={`ListItem.${section.id}`} ref={refCallback} className={classes.mainContentListItem}>
          <ExpansionPanel
            classes={{ root: classes.expansionPanelRoot, expanded: classes.expansionPanelRootExpanded }}
            key={section.id}
            onChange={setExpansionState(section.id)}
            expanded={!collapsedPanels.has(section.id)}
          >
            <AccordionSummary classes={{ root: classes.expansionPanelHeader }}>
              <SvgIcon className={classes.expansionPanelIcon}>
                {collapsedPanels.has(section.id) ? <ArrowDropDown /> : <ArrowDropUp />}
              </SvgIcon>
              <div className={classes.expansionPanelTitleContainer}>
                <Typography className={classes.expansionPanelTitleTypo}>
                  <FormattedHTMLMessage {...section.name} />
                </Typography>
              </div>
            </AccordionSummary>
            <AccordionDetails className={classes.expansionPanel}>
              {section.subsections.map((subsection) => (
                <div className={classes.mainContentSubsection} key={subsection.id}>
                  {subsection.name && (
                    <Typography className={classes.mainContentSubsectionName}>
                      <FormattedHTMLMessage {...subsection.name} />
                    </Typography>
                  )}
                  {subsection.paragraphs.map((paragraph) => renderParagraph(paragraph))}
                </div>
              ))}
              <div onClick={scrollToTop} className={classes.upButton}>
                <SvgIcon>
                  <ArrowUpward fontSize={"small"} />
                </SvgIcon>
                <Typography className={classes.upButtonTypo}>
                  <FormattedHTMLMessage {...messages.GoUp} />
                </Typography>
              </div>
            </AccordionDetails>
          </ExpansionPanel>
        </ListItem>
      ))}
    </>
  );
};

export default withStyles(styles)(MainContent);
